import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Plus, Pencil, Trash2, AlertTriangle } from 'lucide-react';
import { useLiabilities, useCreateLiability, useUpdateLiability, useDeleteLiability, Liability } from '@/hooks/useLiabilities';
import { toast } from 'sonner';
import { useIsMobile } from '@/hooks/use-mobile';
import { LiabilityMobileCard } from '@/components/liability-mobile-card/LiabilityMobileCard';

const CURRENCIES = ['USD', 'EUR', 'GBP', 'KES', 'NGN', 'ZAR', 'INR', 'JPY', 'CAD', 'AUD'];
const LIABILITY_TYPES = ['short_term', 'long_term'];

function LiabilityForm({ liability, onSave, onCancel }: {
  liability?: Liability;
  onSave: (data: any) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(liability?.name ?? '');
  const [type, setType] = useState(liability?.type ?? 'short_term');
  const [amount, setAmount] = useState(liability?.amount?.toString() ?? '0');
  const [remainingBalance, setRemainingBalance] = useState(liability?.remaining_balance?.toString() ?? '0');
  const [interestRate, setInterestRate] = useState(liability?.interest_rate?.toString() ?? '0');
  const [repaymentPeriod, setRepaymentPeriod] = useState(liability?.repayment_period_months?.toString() ?? '');
  const [startDate, setStartDate] = useState(liability?.start_date ?? new Date().toISOString().split('T')[0]);
  const [dueDate, setDueDate] = useState(liability?.due_date ?? '');
  const [currency, setCurrency] = useState(liability?.currency ?? 'USD');
  const [status, setStatus] = useState(liability?.status ?? 'active');
  const [notes, setNotes] = useState(liability?.notes ?? '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSave({
      name: name.trim(),
      type,
      amount: parseFloat(amount) || 0,
      remaining_balance: parseFloat(remainingBalance) || 0,
      interest_rate: parseFloat(interestRate) || 0,
      repayment_period_months: repaymentPeriod ? parseInt(repaymentPeriod) : null,
      start_date: startDate,
      due_date: dueDate || null,
      currency,
      status,
      notes: notes.trim() || null,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="l-name">Liability Name</Label>
        <Input id="l-name" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Car Loan" required />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Type</Label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {LIABILITY_TYPES.map(t => (
                <SelectItem key={t} value={t}>{t.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Currency</Label>
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="l-amount">Total Amount</Label>
          <Input id="l-amount" type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="l-remaining">Remaining Balance</Label>
          <Input id="l-remaining" type="number" step="0.01" value={remainingBalance} onChange={e => setRemainingBalance(e.target.value)} />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="l-interest">Interest Rate (%)</Label>
          <Input id="l-interest" type="number" step="0.01" value={interestRate} onChange={e => setInterestRate(e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="l-period">Repayment Period (months)</Label>
          <Input id="l-period" type="number" value={repaymentPeriod} onChange={e => setRepaymentPeriod(e.target.value)} placeholder="e.g. 24" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="l-start">Start Date</Label>
          <Input id="l-start" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="l-due">Due Date</Label>
          <Input id="l-due" type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
        </div>
      </div>
      {liability && (
        <div className="space-y-2">
          <Label>Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="paid_off">Paid Off</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="space-y-2">
        <Label htmlFor="l-notes">Notes</Label>
        <Input id="l-notes" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional notes" />
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit">{liability ? 'Update' : 'Create'}</Button>
      </DialogFooter>
    </form>
  );
}

export default function Liabilities() {
  const { data: liabilities, isLoading } = useLiabilities();
  const createLiability = useCreateLiability();
  const updateLiability = useUpdateLiability();
  const deleteLiability = useDeleteLiability();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Liability | undefined>();
  const isMobile = useIsMobile();

  const handleSave = async (data: any) => {
    try {
      if (editing) {
        await updateLiability.mutateAsync({ id: editing.id, ...data });
        toast.success('Liability updated');
      } else {
        await createLiability.mutateAsync(data);
        toast.success('Liability added');
      }
      setDialogOpen(false);
      setEditing(undefined);
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteLiability.mutateAsync(id);
      toast.success('Liability deleted');
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const openEdit = (l: Liability) => { setEditing(l); setDialogOpen(true); };
  const openCreate = () => { setEditing(undefined); setDialogOpen(true); };

  const totalLiabilities = (liabilities ?? []).filter(l => l.status === 'active').reduce((sum, l) => sum + Number(l.remaining_balance), 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Liabilities</h1>
        <p className="text-muted-foreground">Track and manage your debts and obligations.</p>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          Total outstanding: <span className="font-bold text-foreground">${totalLiabilities.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
        </p>
        <Dialog open={dialogOpen} onOpenChange={v => { setDialogOpen(v); if (!v) setEditing(undefined); }}>
          <DialogTrigger asChild>
            <Button onClick={openCreate}><Plus className="mr-2 h-4 w-4" /> Add Liability</Button>
          </DialogTrigger>
          <DialogContent className="w-[95vw] sm:w-full sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit Liability' : 'New Liability'}</DialogTitle>
            </DialogHeader>
            <LiabilityForm liability={editing} onSave={handleSave} onCancel={() => setDialogOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader><CardTitle>Your Liabilities</CardTitle></CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : !liabilities?.length ? (
            <p className="text-sm text-muted-foreground">No liabilities tracked yet.</p>
          ) : isMobile ? (
            <div className="space-y-3">
              {liabilities.map(l => (
                <LiabilityMobileCard
                  key={l.id}
                  liability={l}
                  onEdit={openEdit}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Interest</TableHead>
                    <TableHead>Repayment</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Remaining</TableHead>
                    <TableHead className="w-[100px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {liabilities.map(l => {
                    const paidPct = Number(l.amount) > 0 ? ((Number(l.amount) - Number(l.remaining_balance)) / Number(l.amount)) * 100 : 0;
                    return (
                      <TableRow key={l.id} className={l.status === 'paid_off' ? 'opacity-50' : ''}>
                        <TableCell className="font-medium flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4 text-amber-500" />
                          {l.name}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{l.type.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</Badge>
                        </TableCell>
                        <TableCell>{Number(l.interest_rate) > 0 ? `${l.interest_rate}%` : '—'}</TableCell>
                        <TableCell>{l.repayment_period_months ? `${l.repayment_period_months} mo` : '—'}</TableCell>
                        <TableCell>{l.due_date || '—'}</TableCell>
                        <TableCell>
                          <Badge variant={l.status === 'active' ? 'destructive' : 'default'}>
                            {l.status === 'active' ? 'Active' : 'Paid Off'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div>
                            <span className="font-mono">{Number(l.remaining_balance).toLocaleString(undefined, { style: 'currency', currency: l.currency })}</span>
                            <div className="w-full bg-muted rounded-full h-1.5 mt-1">
                              <div className="bg-primary rounded-full h-1.5" style={{ width: `${Math.min(paidPct, 100)}%` }} />
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => openEdit(l)}><Pencil className="h-4 w-4" /></Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="icon"><Trash2 className="h-4 w-4 text-destructive" /></Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete "{l.name}"?</AlertDialogTitle>
                                  <AlertDialogDescription>This will permanently remove this liability.</AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDelete(l.id)}>Delete</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
